import React, { useState } from 'react';
import { useSeo } from '../lib/seo';
import { Helmet } from 'react-helmet-async';
import { Lock, LogIn, UserPlus, Check, Award, KeyRound } from 'lucide-react';
import partnerApi, { setPartnerSession } from '../lib/partnerApi';

/**
 * The login wall for /partners/*. Shown when the visitor has no valid
 * partner JWT. Two tabs:
 *   1. Sign in — existing partners
 *   2. Apply for access — new businesses request access; admin reviews
 *
 * On successful login we hand control back to PartnerProtected by writing
 * the JWT and reloading the same route — no router push needed and any
 * deep link the visitor came from re-renders straight through.
 */
export default function PartnerGate() {
  useSeo('Trade Partner Sign In | goLeicestershire');
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'apply' ? 'apply' : 'signin');

  return (
    <div className="bg-base min-h-[80vh]" data-testid="partner-gate">
      <Helmet>
        <title>Trade Partners — Sign in or apply for access</title>
        <meta name="description" content="Sign in to the GoLeicestershire Trade Partners portal or apply for access." />
      </Helmet>

      <section className="bg-heritage text-base py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-3">For tourism businesses</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4">
            Trade <em className="not-italic text-mustard">Partners</em>
          </h1>
          <p className="text-base/85 text-lg max-w-2xl leading-relaxed">
            The partner portal is by invitation. Sign in if you&apos;ve already been approved,
            or request access and our partnerships team will be in touch.
          </p>
        </div>
      </section>

      <section className="max-w-[640px] mx-auto px-6 lg:px-12 py-12 md:py-16">
        <div className="flex border-b border-charcoal/15 mb-8" role="tablist">
          <TabButton active={tab === 'signin'} onClick={() => setTab('signin')} testid="gate-tab-signin">
            <LogIn size={15} /> Sign in
          </TabButton>
          <TabButton active={tab === 'apply'} onClick={() => setTab('apply')} testid="gate-tab-apply">
            <UserPlus size={15} /> Apply for access
          </TabButton>
        </div>

        {tab === 'signin' ? <SignInForm /> : <ApplyForm onSwitchToSignIn={() => setTab('signin')} />}

        <div className="mt-12 pt-8 border-t border-charcoal/10 text-sm text-charcoal/65 leading-relaxed flex items-start gap-3">
          <Award size={18} className="text-heritage shrink-0 mt-0.5" />
          <div>
            Looking for the <strong>public Tourism Awards voting</strong>? That&apos;s open to everyone
            — head to <a href="/awards" className="text-heritage underline font-medium">goleicestershire.com/awards</a> and
            no sign-in is needed.
          </div>
        </div>
      </section>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
        active ? 'border-heritage text-heritage' : 'border-transparent text-charcoal/55 hover:text-charcoal'
      }`}
    >
      {children}
    </button>
  );
}

function SignInForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('signin'); // 'signin' | 'forgot' | 'forgot-sent'

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      const { data } = await partnerApi.post('/partners/login', { email, password });
      setPartnerSession({ token: data.token, partner: data.partner });
      window.location.reload();
    } catch (e2) {
      // Fallback: admins can sign in here with their /admin credentials too.
      // We only try the admin endpoint if the partner endpoint specifically
      // rejected the credentials (401) — a 4xx with status='approved' etc.
      // means the email IS a partner account and the message is correct.
      if (e2?.response?.status === 401) {
        try {
          const { data: adminData } = await partnerApi.post('/auth/login', { email, password });
          // Store under the admin token key so PartnerProtected enters
          // admin-view mode and the regular admin nav also unlocks.
          localStorage.setItem('gl_admin_token', adminData.token);
          localStorage.setItem('gl_admin_role', adminData.role || 'admin');
          window.location.reload();
          return;
        } catch (adminErr) {
          setErr('Invalid email or password');
        }
      } else {
        setErr(formatErr(e2));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await partnerApi.post('/partners/forgot-password', { email });
      setMode('forgot-sent');
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'forgot-sent') {
    return (
      <div className="text-center py-6" data-testid="forgot-sent">
        <Check size={36} className="text-heritage mx-auto mb-3" />
        <h3 className="font-display text-2xl mb-2">Check your inbox</h3>
        <p className="text-charcoal/70 leading-relaxed mb-5 max-w-md mx-auto">
          If we have an active account for <strong>{email}</strong>, we&apos;ve emailed a
          password reset link. It&apos;s good for 7 days and can only be used once.
        </p>
        <button
          type="button"
          onClick={() => { setMode('signin'); setErr(''); }}
          className="text-sm font-semibold text-heritage hover:underline"
          data-testid="forgot-back-to-signin"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  if (mode === 'forgot') {
    return (
      <form onSubmit={submitForgot} className="space-y-4" data-testid="partner-forgot-form">
        <div className="flex items-center gap-2 text-sm text-charcoal/70 mb-2">
          <KeyRound size={14} className="text-heritage" /> Enter your email and we&apos;ll send you a reset link.
        </div>
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="forgot-email" required />
        {err && <div className="text-sm text-red-700" data-testid="forgot-error">{err}</div>}
        <button
          type="submit"
          disabled={busy}
          data-testid="forgot-submit"
          className="w-full bg-heritage text-base font-semibold rounded-full py-3 hover:bg-charcoal disabled:opacity-50 transition-colors"
        >
          {busy ? 'Sending\u2026' : 'Email me a reset link'}
        </button>
        <button
          type="button"
          onClick={() => { setMode('signin'); setErr(''); }}
          className="block w-full text-xs text-charcoal/55 hover:text-charcoal text-center"
          data-testid="forgot-cancel"
        >
          ← Back to sign in
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="partner-signin-form">
      <div className="flex items-center gap-2 text-sm text-charcoal/70 mb-2">
        <Lock size={14} className="text-heritage" /> Existing partners — sign in with your email and password.
      </div>
      <Field label="Email" type="email" value={email} onChange={setEmail} testid="signin-email" required />
      <Field label="Password" type="password" value={password} onChange={setPassword} testid="signin-password" required />
      {err && <div className="text-sm text-red-700" data-testid="signin-error">{err}</div>}
      <button
        type="submit"
        disabled={busy}
        data-testid="signin-submit"
        className="w-full bg-heritage text-base font-semibold rounded-full py-3 hover:bg-charcoal disabled:opacity-50 transition-colors"
      >
        {busy ? 'Signing in\u2026' : 'Sign in'}
      </button>
      <button
        type="button"
        onClick={() => { setMode('forgot'); setErr(''); setPassword(''); }}
        data-testid="forgot-password-link"
        className="block w-full text-xs text-charcoal/65 hover:text-heritage text-center underline"
      >
        Forgot your password?
      </button>
      <p className="text-xs text-charcoal/55 text-center pt-2">
        GoLeicestershire admins: sign in here with your <code className="bg-stone px-1 py-0.5 rounded">/admin</code> credentials too.
      </p>
    </form>
  );
}

function ApplyForm({ onSwitchToSignIn }) {
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await partnerApi.post('/partners/apply', {
        email,
        business_name: businessName,
        contact_name: contactName,
      });
      setSent(true);
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="text-center py-6" data-testid="apply-sent">
        <Check size={36} className="text-heritage mx-auto mb-3" />
        <h3 className="font-display text-2xl mb-2">Thanks — we&apos;ve got it</h3>
        <p className="text-charcoal/70 leading-relaxed mb-5 max-w-md mx-auto">
          Our partnerships team will review your request and email you a password-set link
          within a working day or two.
        </p>
        <button
          type="button"
          onClick={onSwitchToSignIn}
          className="text-sm font-semibold text-heritage hover:underline"
          data-testid="apply-back-to-signin"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="partner-apply-form">
      <div className="flex items-center gap-2 text-sm text-charcoal/70 mb-2">
        <UserPlus size={14} className="text-heritage" /> New here? Tell us a little about your business and we&apos;ll set you up.
      </div>
      <Field label="Your name" value={contactName} onChange={setContactName} testid="apply-contact-name" required />
      <Field label="Business name" value={businessName} onChange={setBusinessName} testid="apply-business-name" required />
      <Field label="Email" type="email" value={email} onChange={setEmail} testid="apply-email" required />
      {err && <div className="text-sm text-red-700" data-testid="apply-error">{err}</div>}
      <button
        type="submit"
        disabled={busy}
        data-testid="apply-submit"
        className="w-full bg-heritage text-base font-semibold rounded-full py-3 hover:bg-charcoal disabled:opacity-50 transition-colors"
      >
        {busy ? 'Sending\u2026' : 'Request access'}
      </button>
      <p className="text-xs text-charcoal/55 text-center">
        Your details are only used to set up your partner account.
      </p>
    </form>
  );
}

function Field({ label, value, onChange, type = 'text', testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">
        {label}{required && <span className="text-heritage ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-heritage"
      />
    </div>
  );
}

function formatErr(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return 'Sorry, something went wrong. Please try again.';
}
